import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Tab, Tabs, Table, ListGroup, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPhone, faEnvelope, faCalendar, faCheckCircle, faTimesCircle, faExclamationCircle, faExternalLinkAlt, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { callQuery, LIDIA_GET_PARTICIPANT_DETAILS, LIDIA_GET_CONVERSATION, LidiaParticipant, LidiaParticipantDetails, LidiaSMSMessage, LidiaAlert, PATIENT_VISIT_BY_CSN } from '../../amplify/appsync/query';
import { callWrite, LIDIA_UPDATE_PARTICIPANT_STATUS, LIDIA_ACKNOWLEDGE_ALERT, LIDIA } from '../../amplify/appsync/write';
import { Auth } from 'aws-amplify';
import { getLocalDateStrFromDate, getLocalTimeStrFromDate, parseAsUTC } from '../../utils/time';
import { dashboardLLMCache } from '../dashboard/llm/cache';
import { PatientVisit } from '../../amplify/appsync/search-patients';
import { LidiaDashboard } from '../dashboard/llm/lidia/LidiaDashboard';

export const LidiaParticipantDetail = (props: {
    user: any,
    ws: string,
    tenant: string,
}) => {
    const { studyId } = useParams<{ studyId: string }>();
    const navigate = useNavigate();
    const [patient, setPatient] = useState<PatientVisit | null>(null);
    const [participant, setParticipant] = useState<LidiaParticipant | null>(null);
    const [participantDetails, setParticipantDetails] = useState<LidiaParticipantDetails | null>(null);
    const [conversationHistory, setConversationHistory] = useState<LidiaSMSMessage[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);

    // Alert acknowledgment state
    const [showAckModal, setShowAckModal] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState<LidiaAlert | null>(null);
    const [ackNotes, setAckNotes] = useState('');
    const [resolutionAction, setResolutionAction] = useState('');

    useEffect(() => {
        if (!studyId) {
            navigate('/lidia-dashboard');
            return;
        }
        loadParticipantDetails();
    }, [studyId]);

    const loadParticipantDetails = async () => {
        if (!studyId) return;

        setIsLoading(true);
        try {
            const details = await callQuery({
                operation: LIDIA_GET_PARTICIPANT_DETAILS,
                study_id: studyId
            });

            if (!details) {
                console.error('Participant not found');
                navigate('/lidia-dashboard');
                return;
            }

            setParticipant(details);
            setParticipantDetails(details);

            callQuery({
                operation: PATIENT_VISIT_BY_CSN,
                csn: details.csn,
            })
            .then((info: PatientVisit[]) => {
                if (info && info.length > 0) {
                    setPatient(info[0]);
                }
            });

            // Load conversation history from cache
            if (details && details.csn) {
                try {
                    const history = await dashboardLLMCache.get(LIDIA, { csn: details.csn, mrn: details.mrn } as any, 'default');
                    setConversationHistory(history || []);
                } catch (error) {
                    console.error('Error loading conversation history:', error);
                    setConversationHistory([]);
                }
            }
        } catch (error) {
            console.error('Error loading participant details:', error);
            navigate('/lidia-dashboard');
        } finally {
            setIsLoading(false);
        }
    };

    const updateStatus = async (newStatus: 'ACTIVE' | 'WITHDRAWN' | 'COMPLETED') => {
        if (!studyId || !window.confirm(`Are you sure you want to change status to ${newStatus}?`)) {
            return;
        }

        setIsUpdating(true);
        try {
            await callWrite({
                operation: LIDIA_UPDATE_PARTICIPANT_STATUS,
                study_id: studyId,
                status: newStatus
            });

            alert(`Status updated to ${newStatus}`);
            navigate('/lidia-dashboard');
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update status');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleBack = () => {
        navigate('/lidia-dashboard');
    };

    const handleAcknowledgeAlert = async () => {
        if (!selectedAlert) return;

        try {
            const user = await Auth.currentAuthenticatedUser();
            await callWrite({
                operation: LIDIA_ACKNOWLEDGE_ALERT,
                alert_id: selectedAlert.alert_id,
                username: user.username,
                acknowledgment_notes: ackNotes || null,
                resolution_action: resolutionAction || null
            });

            // Close modal and reset state
            setShowAckModal(false);
            setSelectedAlert(null);
            setAckNotes('');
            setResolutionAction('');

            // Reload participant details to get updated alert counts
            await loadParticipantDetails();
        } catch (error) {
            console.error('Error acknowledging alert:', error);
            alert('Failed to acknowledge alert');
        }
    };

    const formatDate = (dateStr?: string | null) => {
        if (!dateStr) return 'N/A';
        try {
            const date = parseAsUTC(dateStr);
            return `${getLocalDateStrFromDate(date)} ${getLocalTimeStrFromDate(date)}`;
        } catch {
            return dateStr;
        }
    };

    const getAlertCategoryBadge = (category: string) => {
        const variants: Record<string, string> = {
            SAFETY: 'danger',
            PROTOCOL: 'warning',
            ENGAGEMENT: 'info',
            DATA_QUALITY: 'secondary',
            ADMINISTRATIVE: 'light',
            POSITIVE: 'success'
        };
        return <Badge bg={variants[category] || 'secondary'}>{category}</Badge>;
    };

    const getAlertSeverityBadge = (severity?: string | null) => {
        if (!severity) return null;
        const variants: Record<string, string> = {
            critical: 'danger',
            high: 'danger',
            medium: 'warning',
            low: 'info',
            info: 'secondary'
        };
        return <Badge bg={variants[severity] || 'secondary'}>{severity.toUpperCase()}</Badge>;
    };

    const getStatusBadge = (status: string) => {
        const variants: Record<string, string> = {
            ACTIVE: 'success',
            WITHDRAWN: 'warning',
            COMPLETED: 'secondary'
        };
        return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
    };

    const getMessageTypeBadge = (type?: string) => {
        const labels: Record<string, string> = {
            WR: 'Waiting Room',
            ROOM: 'In Room',
            PDM: 'Post-Discharge',
            ACTIONABLE_ITEM: 'Actionable Item',
            NEXT_STEP: 'Next Step'
        };
        return <Badge bg="info">{labels[type || ''] || type}</Badge>;
    };

    const redirectToDashboard = () => {
        if (patient) {
            window.open(`/dashboard/${patient.internal_csn}`, "_blank");
        }
    };

    if (isLoading) {
        return (
            <Container fluid style={{marginTop: '80px'}}>
                <p>Loading participant details...</p>
            </Container>
        );
    }

    if (!participantDetails || !participant) {
        return (
            <Container fluid style={{marginTop: '80px'}}>
                <Button variant="outline-secondary" onClick={handleBack} className="mb-3">
                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                </Button>
                <p>Participant not found</p>
            </Container>
        );
    }

    return (
        <Container fluid style={{marginTop: '80px'}}>
            {/* <Row className="mb-3">
                <Col>
                    <Button variant="outline-secondary" onClick={handleBack}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Back to List
                    </Button>
                </Col>
            </Row> */}

            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Body>
                            <Row>
                                <Col>
                                    <h4>{participantDetails.study_id}</h4>
                                </Col>
                                <Col className="text-right">
                                    {getStatusBadge(participantDetails.status)}
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <p className="mb-1">
                                        <small>
                                            <strong>MRN:</strong> {participantDetails.mrn} &bull; <strong>CSN:</strong> 
                                            <a
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    redirectToDashboard();
                                                }}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-decoration-none"
                                                title="Open patient dashboard in new tab"
                                            >{' '}
                                                {participant.csn}{' '}
                                                <FontAwesomeIcon
                                                    icon={faExternalLinkAlt}
                                                    size="sm"
                                                    className="text-muted"
                                                />
                                            </a>
                                            {participantDetails.phone_number && (
                                                <>{' '}&bull;{' '}
                                                    <FontAwesomeIcon icon={faPhone} /> {participantDetails.phone_number}
                                                </>
                                            )}
                                            {' '}&bull; <strong>Enrolled:</strong> {formatDate(participantDetails.enrollment_date)}
                                            {participantDetails.end_date && (
                                                <>{' '}&bull; <strong>End Date:</strong> {formatDate(participantDetails.end_date)}</>
                                            )}
                                            {' '}&bull; <strong>Last Activity:</strong> {formatDate(participantDetails.last_activity_date)}
                                        </small>
                                    </p>
                                </Col>
                            </Row>

                            {/* Conversation Overview */}
                            {participantDetails.conversations && participantDetails.conversations.length > 0 && (
                                <Row className="mt-3">
                                    <Col>
                                        <small>
                                            <Table responsive size="sm">
                                                <thead>
                                                    <tr>
                                                        <th>Messages</th>
                                                        <th>Sent</th>
                                                        <th>Received</th>
                                                        <th>Errors</th>
                                                        <th>Alerts</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {participantDetails.conversations.map((conv, idx) => (
                                                        <tr key={idx}>
                                                            <td>{conv.message_count}</td>
                                                            <td>{conv.sent_count}</td>
                                                            <td>{conv.received_count}</td>
                                                            <td>
                                                                {!!conv.error_count && conv.error_count > 0 ? (
                                                                    <Badge bg="danger" title={conv.last_error || 'Error occurred'}>
                                                                        {conv.error_count}
                                                                    </Badge>
                                                                ) : (
                                                                    <span className="text-muted">None</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {!!participant.unacknowledged_alerts && participant.unacknowledged_alerts > 0 ? (
                                                                    <Badge bg="warning" className="d-flex align-items-center gap-1 justify-content-center">
                                                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                                                        {participant.unacknowledged_alerts}
                                                                    </Badge>
                                                                ) : (
                                                                    <span className="text-muted">None</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </small>
                                    </Col>
                                </Row>
                            )}

                            <Row className="mt-3">
                                <Col>
                                    {participantDetails.status === 'ACTIVE' && (
                                        <>
                                            <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => updateStatus('WITHDRAWN')}
                                                disabled={isUpdating}
                                                className="me-2"
                                            >
                                                Withdraw
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => updateStatus('COMPLETED')}
                                                disabled={isUpdating}
                                            >
                                                Mark Complete
                                            </Button>
                                        </>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Tabbed Interface */}
            <Row>
                <Col>
                    <Tabs defaultActiveKey="messages" className="mb-3">
                        <Tab eventKey="messages" title="Message History">
                            <Card>
                                <Card.Body>
                                    {patient && (
                                        <LidiaDashboard
                                            {...props}
                                            patientVisit={patient}
                                        />
                                    )}
                                    {/* {conversationHistory.length > 0 ? (
                                        <div className="d-flex flex-column gap-3">
                                            {conversationHistory.map((message, idx) => {
                                                const isLidia = message.role === 'LIDIA';
                                                return (
                                                    <div
                                                        key={idx}
                                                        className={`d-flex ${isLidia ? 'justify-content-start' : 'justify-content-end'}`}
                                                    >
                                                        <div
                                                            className={`p-3 rounded ${isLidia ? 'bg-primary text-white' : 'bg-light'}`}
                                                            style={{ maxWidth: '70%' }}
                                                        >
                                                            <div className="mb-2">
                                                                <small>
                                                                    <strong>{message.role}</strong> - {formatDate(message.timestamp)}
                                                                </small>
                                                                {message.message_type && (
                                                                    <span className="ms-2">{getMessageTypeBadge(message.message_type)}</span>
                                                                )}
                                                            </div>
                                                            <p className="mb-0">{message.content}</p>
                                                            {message.metadata?.wait_time !== undefined && (
                                                                <small className="d-block mt-2 opacity-75">
                                                                    Wait time: {message.metadata.wait_time} days
                                                                </small>
                                                            )}
                                                            {message.metadata?.expect_patient_reply === 'yes' && (
                                                                <small className="d-block mt-1 opacity-75">
                                                                    Expecting patient reply
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-muted">No message history available</p>
                                    )} */}
                                </Card.Body>
                            </Card>
                        </Tab>

                        <Tab
                            eventKey="alerts"
                            title={
                                <>
                                    Alerts {participantDetails.alerts && participantDetails.alerts.filter(a => !a.acknowledged_at).length > 0 && (
                                        <Badge bg="warning" className="ms-1">
                                            {participantDetails.alerts.filter(a => !a.acknowledged_at).length}
                                        </Badge>
                                    )}
                                </>
                            }
                        >
                            <Card>
                                <Card.Body>
                                    {participantDetails.alerts && participantDetails.alerts.length > 0 ? (
                                        <div className="d-flex flex-column gap-3">
                                            {participantDetails.alerts.map((alert) => (
                                                <Card
                                                    key={alert.alert_id}
                                                    className={!alert.acknowledged_at ? 'border-warning' : ''}
                                                >
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div>
                                                                <h5 className="mb-2">{alert.alert_title}</h5>
                                                                <div className="d-flex gap-2 mb-2">
                                                                    {getAlertCategoryBadge(alert.alert_category)}
                                                                    {getAlertSeverityBadge(alert.severity)}
                                                                    {alert.conversation_phase && (
                                                                        <Badge bg="info">{alert.conversation_phase}</Badge>
                                                                    )}
                                                                    <Badge bg="light" text="dark">{alert.alert_source}</Badge>
                                                                </div>
                                                            </div>
                                                            <div className="text-end">
                                                                <small className="text-muted">{formatDate(alert.alert_timestamp)}</small>
                                                            </div>
                                                        </div>

                                                        <p className="mb-2"><strong>Alert:</strong> {alert.alert_message}</p>

                                                        {alert.context_message && (
                                                            <p className="mb-2 text-muted"><strong>Context:</strong> {alert.context_message}</p>
                                                        )}

                                                        {alert.acknowledged_at ? (
                                                            <div className="mt-3 pt-3 border-top">
                                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                                                                    <strong>Acknowledged</strong>
                                                                </div>
                                                                <p className="mb-1"><strong>By:</strong> {alert.acknowledged_by}</p>
                                                                <p className="mb-1"><strong>At:</strong> {formatDate(alert.acknowledged_at)}</p>
                                                                {alert.resolution_action && (
                                                                    <p className="mb-1"><strong>Action:</strong> {alert.resolution_action}</p>
                                                                )}
                                                                {alert.acknowledgment_notes && (
                                                                    <p className="mb-0"><strong>Notes:</strong> {alert.acknowledgment_notes}</p>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="mt-3">
                                                                <Button
                                                                    size="sm"
                                                                    variant="primary"
                                                                    onClick={() => {
                                                                        setSelectedAlert(alert);
                                                                        setShowAckModal(true);
                                                                    }}
                                                                >
                                                                    Acknowledge Alert
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted">No alerts</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Tab>
                    </Tabs>
                </Col>
            </Row>

            {/* Acknowledgment Modal */}
            <Modal show={showAckModal} onHide={() => setShowAckModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Acknowledge Alert</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedAlert && (
                        <>
                            <p><strong>Alert:</strong> {selectedAlert.alert_title}</p>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Resolution Action</Form.Label>
                                    <Form.Select
                                        value={resolutionAction}
                                        onChange={(e) => setResolutionAction(e.target.value)}
                                    >
                                        <option value="">Select action...</option>
                                        <option value="NO_ACTION">No Action Required</option>
                                        <option value="CONTACTED_PATIENT">Contacted Patient</option>
                                        <option value="ESCALATED">Escalated to Provider</option>
                                        <option value="PROTOCOL_ADJUSTED">Protocol Adjusted</option>
                                        <option value="MONITORING">Monitoring</option>
                                        <option value="RESOLVED">Resolved</option>
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Notes</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={ackNotes}
                                        onChange={(e) => setAckNotes(e.target.value)}
                                        placeholder="Add any notes about this alert and actions taken..."
                                    />
                                </Form.Group>
                            </Form>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAckModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleAcknowledgeAlert}>
                        Acknowledge
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default LidiaParticipantDetail;
